import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useContract } from '../hooks/useContracts';
import { contractService } from '../services/contractService';
import { documentService } from '../services/documentService';
import { reviewService } from '../services/reviewService';
import { getNextStates } from '@shared/stateMachine';
import { ArrowLeftIcon, DocumentArrowDownIcon, ArrowTopRightOnSquareIcon, ArchiveBoxIcon, PencilIcon, PaperAirplaneIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines, faSignature, faCircleCheck, faBoxArchive } from '@fortawesome/free-solid-svg-icons';
import { useConfirm } from '../hooks/useConfirm';
import { ReviewerPicker } from './ReviewerPicker';

export function ContractDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { contract, loading, reload } = useContract(id!);
  const [processing, setProcessing] = useState(false);
  const [isReviewer, setIsReviewer] = useState(false);
  const [showReviewerPicker, setShowReviewerPicker] = useState(false);
  const [showMoveToMenu, setShowMoveToMenu] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();

  // Check if current user is a reviewer
  useEffect(() => {
    reviewService.isReviewer().then(setIsReviewer);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Contract not found</h2>
          <button
            onClick={() => navigate('/contracts')}
            className="text-primary-600 hover:text-primary-700"
          >
            Return to contracts
          </button>
        </div>
      </div>
    );
  }

  const nextStates = getNextStates(contract.status);

  const handleStatusChange = async (newStatus: string) => {
    setProcessing(true);
    const toastId = toast.loading(`Moving to ${newStatus}...`);
    
    try {
      await contractService.updateStatus(id!, newStatus as any);
      toast.success(`Contract status updated to ${newStatus}!`, { id: toastId });
      reload();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status. Please try again.', { id: toastId });
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerateContract = async () => {
    setProcessing(true);
    const toastId = toast.loading('Generating contract document...');
    
    try {
      const document = await documentService.generateContractDocument(id!);
      toast.success('Contract generated successfully!', { id: toastId });
      reload();
      
      // Open the document in a new tab
      if (document.url) {
        window.open(document.url, '_blank');
      }
    } catch (error) {
      console.error('Failed to generate contract:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate contract', { id: toastId });
    } finally {
      setProcessing(false);
    }
  };

  const handleArchive = async () => {
    const confirmed = await confirm({
      title: 'Archive Contract',
      message: 'Are you sure you want to archive this contract? You can still view it later in the archived contracts list.',
      confirmText: 'Archive',
      confirmStyle: 'danger',
    });

    if (!confirmed) return;

    setProcessing(true);
    const toastId = toast.loading('Archiving contract...');
    
    try {
      await contractService.updateContract(id!, { status: 'archived' });
      toast.success('Contract archived successfully!', { id: toastId });
      reload();
    } catch (error) {
      console.error('Failed to archive contract:', error);
      toast.error('Failed to archive contract. Please try again.', { id: toastId });
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitForReview = () => {
    // Open reviewer picker modal
    setShowReviewerPicker(true);
  };

  const handleConfirmSubmitForReview = async (reviewers: Array<{ email: string; name: string }>) => {
    setProcessing(true);
    const toastId = toast.loading('Submitting contract for review...');
    
    try {
      await reviewService.submitForReview(id!, reviewers);
      toast.success(`Contract submitted for review! ${reviewers.length} reviewer(s) notified via email.`, { id: toastId });
      reload();
    } catch (error) {
      console.error('Failed to submit for review:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to submit for review', { id: toastId });
    } finally {
      setProcessing(false);
    }
  };

  const handleApprove = async () => {
    const confirmed = await confirm({
      title: 'Approve Contract',
      message: 'This will move the contract to "signature" status. The contract is ready for execution.',
      confirmText: 'Approve',
    });

    if (!confirmed) return;

    setProcessing(true);
    const toastId = toast.loading('Approving contract...');
    
    try {
      await reviewService.approveContract(id!);
      toast.success('Contract approved! Status updated to "signature".', { id: toastId });
      reload();
    } catch (error) {
      console.error('Failed to approve contract:', error);
      toast.error('Failed to approve contract. Please try again.', { id: toastId });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    const confirmed = await confirm({
      title: 'Reject Contract',
      message: 'This will move the contract back to "draft" status for revisions. Continue?',
      confirmText: 'Reject',
      confirmStyle: 'danger',
    });

    if (!confirmed) return;

    setProcessing(true);
    const toastId = toast.loading('Rejecting contract...');
    
    try {
      await reviewService.rejectContract(id!);
      toast.success('Contract rejected. Status updated to "draft" for revisions.', { id: toastId });
      reload();
    } catch (error) {
      console.error('Failed to reject contract:', error);
      toast.error('Failed to reject contract. Please try again.', { id: toastId });
    } finally {
      setProcessing(false);
    }
  };

  const statusColors: Record<string, string> = {
    draft: 'bg-yellow-100 text-yellow-700',
    review: 'bg-blue-100 text-blue-700',
    signature: 'bg-purple-100 text-purple-700',
    executed: 'bg-green-100 text-green-700',
    archived: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/contracts')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back to contracts
        </button>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              {contract.title && (
                <h1 className="text-3xl font-bold text-gray-900">{contract.title}</h1>
              )}
              <p className="mt-2 text-xl text-gray-700 font-semibold">{contract.clientName}</p>
              <p className="mt-1 text-sm text-gray-600">
                Created: {new Date(contract.createdAt).toLocaleDateString()}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              {contract.templateType && (
                <span className="px-3 py-1 text-xs font-bold rounded-full bg-primary-100 text-primary-700 border border-primary-300">
                  {contract.templateType.toUpperCase()}
                </span>
              )}
              <span
                className={`px-4 py-2 text-sm font-medium rounded-lg ${
                  statusColors[contract.status] || 'bg-gray-100 text-gray-700'
                }`}
              >
                {contract.status}
              </span>
            </div>
          </div>

          <div className="space-y-8">
            {contract.executiveSummary && (
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Executive Summary</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{contract.executiveSummary}</p>
              </section>
            )}

            {contract.deliverables && contract.deliverables.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                  Deliverables ({contract.deliverables.length})
                </h2>
                <div className="space-y-3">
                  {contract.deliverables.map((deliverable) => (
                    <div key={deliverable.id} className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-medium text-gray-900">{deliverable.title}</h3>
                      <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{deliverable.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {contract.payments && contract.payments.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                  Payment Schedule ({contract.payments.length})
                </h2>
                <div className="space-y-3">
                  {contract.payments.map((payment) => (
                    <div key={payment.id} className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-medium text-gray-900 text-lg">
                        ${payment.amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                      </h3>
                      {payment.milestone && (
                        <p className="text-sm font-semibold text-gray-700 mt-1">{payment.milestone}</p>
                      )}
                      <p className="text-sm text-gray-600 mt-1">{payment.description}</p>
                    </div>
                  ))}
                  <div className="p-4 bg-primary-50 border-2 border-primary-200 rounded-lg">
                    <h3 className="font-bold text-lg text-gray-900">
                      Total: ${contract.payments.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </h3>
                  </div>
                </div>
              </section>
            )}
          </div>

          {/* Generated Document Section */}
          {contract.metadata && (contract.metadata as any).generated_document && (
            <div className="mt-6">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-green-900">✓ Document Generated</h3>
                    <p className="text-sm text-green-800 mt-1">
                      {(contract.metadata as any).generated_document.file_name}
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      Generated on {new Date((contract.metadata as any).generated_document.generated_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={(contract.metadata as any).generated_document.web_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      Open in SharePoint
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Bar */}
          <div className="mt-8 pt-6 border-t-2 border-gray-200">
            <div className="flex items-center justify-between gap-6">
              {/* Primary Actions - Left */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate(`/contracts/${id}/edit`)}
                    disabled={processing || contract.status !== 'draft'}
                    className="flex items-center gap-2 px-5 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title={contract.status !== 'draft' ? 'Move to Draft to edit' : 'Edit contract'}
                  >
                    <PencilIcon className="w-5 h-5" />
                    Edit
                  </button>

                  <button
                    onClick={handleGenerateContract}
                    disabled={processing || contract.status !== 'draft'}
                    className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 shadow-sm hover:shadow disabled:opacity-50 transition-all"
                    title={contract.status !== 'draft' ? 'Move to Draft to regenerate' : 'Generate document'}
                  >
                    <DocumentArrowDownIcon className="w-5 h-5" />
                    {contract.metadata && (contract.metadata as any).generated_document ? 'Regenerate' : 'Generate Document'}
                  </button>

                  {/* Submit for Review button (only show in draft status) */}
                  {contract.status === 'draft' && (
                    <button
                      onClick={handleSubmitForReview}
                      disabled={processing}
                      className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 shadow-sm hover:shadow disabled:opacity-50 transition-all"
                    >
                      <PaperAirplaneIcon className="w-5 h-5" />
                      Submit for Review
                    </button>
                  )}
                </div>
                {contract.status !== 'draft' && (
                  <p className="text-xs text-gray-500 italic">
                    💡 Use "Move to → Draft" to edit or regenerate this contract
                  </p>
                )}
              </div>

              {/* Status Actions - Center/Right */}
              <div className="flex items-center gap-4">
                {/* Approve/Reject buttons (only show in review status and if user is a reviewer) */}
                {contract.status === 'review' && isReviewer && (
                  <div className="flex items-center gap-3 px-4 py-2 bg-blue-50 rounded-lg border-2 border-blue-200">
                    <span className="text-sm font-medium text-blue-700">Review:</span>
                    <div className="flex gap-2">
                      <button
                        onClick={handleApprove}
                        disabled={processing}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        <CheckCircleIcon className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={handleReject}
                        disabled={processing}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        <XCircleIcon className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </div>
              )}

              {/* Move to dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowMoveToMenu(!showMoveToMenu)}
                    disabled={processing}
                    className="flex items-center gap-2 px-4 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 transition-colors"
                  >
                    Move to
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showMoveToMenu && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setShowMoveToMenu(false)}
                      />
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-20">
                        {/* Draft option (always available to revert) */}
                        {contract.status !== 'draft' && (
                          <button
                            onClick={() => {
                              handleStatusChange('draft');
                              setShowMoveToMenu(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors flex items-center gap-2"
                          >
                            <FontAwesomeIcon icon={faFileLines} className="w-4 h-4" />
                            Draft
                          </button>
                        )}

                        {/* Valid next states from state machine */}
                        {nextStates.filter(s => s !== 'review' && s !== 'archived' && s !== 'draft').map((status) => (
                          <button
                            key={status}
                            onClick={() => {
                              handleStatusChange(status);
                              setShowMoveToMenu(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 capitalize transition-colors flex items-center gap-2"
                          >
                            {status === 'signature' && <FontAwesomeIcon icon={faSignature} className="w-4 h-4" />}
                            {status === 'executed' && <FontAwesomeIcon icon={faCircleCheck} className="w-4 h-4" />}
                            {status}
                          </button>
                        ))}

                        {/* Archive option (always available) */}
                        {contract.status !== 'archived' && (
                          <>
                            <div className="border-t border-gray-100 my-1" />
                            <button
                              onClick={() => {
                                handleArchive();
                                setShowMoveToMenu(false);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                            >
                              <FontAwesomeIcon icon={faBoxArchive} className="w-4 h-4" />
                              Archive
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      <ConfirmDialog />

      {/* Reviewer Picker Modal */}
      <ReviewerPicker 
        isOpen={showReviewerPicker}
        onClose={() => setShowReviewerPicker(false)}
        onSubmit={handleConfirmSubmitForReview}
      />
    </div>
  );
}

